import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInstall } from "@/context/InstallContext";
import { useAuth } from "@/context/AuthContext";
import { Download, Share, Plus, Sparkles, CheckCircle2, ExternalLink, Info } from "lucide-react";

/**
 * Public /install destination for the marketing site's "Download App" buttons.
 * Reuses the existing PWA InstallContext — no separate install pipeline.
 */
export default function Install() {
  const navigate = useNavigate();
  const { canInstall, install, hasDeferred, isIos, isStandalone } = useInstall();
  const { user } = useAuth();
  const openApp = () => navigate(user ? "/" : "/login");
  const openLabel = user ? "Open NContro" : "Sign in to NContro";
  const [installing, setInstalling] = useState(false);
  const [installOutcome, setInstallOutcome] = useState(null);
  const outcomeMessage = {
    accepted: "Installation accepted. Your browser will finish installing NContro.",
    dismissed: "Installation dismissed. You can still continue in this browser.",
    failed: "The installation prompt could not be opened. You can still continue in this browser.",
    unavailable: "No installation prompt is available right now. You can still continue in this browser.",
  }[installOutcome];

  // Support flags the InstallContext cannot answer:
  //  - unsupported browser = not iOS AND we never captured a BIP event AND the
  //    browser doesn't expose BeforeInstallPromptEvent at all.
  const [supportsBip, setSupportsBip] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // If we already have a deferred event, the browser obviously supports it.
    if (hasDeferred) { setSupportsBip(true); return; }
    // Chromium exposes this constructor. Safari/Firefox do not.
    const has = "BeforeInstallPromptEvent" in window || "onbeforeinstallprompt" in window;
    setSupportsBip(has);
  }, [hasDeferred]);

  const handleInstall = async () => {
    if (!hasDeferred) return;
    setInstalling(true);
    setInstallOutcome(null);
    try {
      setInstallOutcome(await install());
    } catch {
      setInstallOutcome("failed");
    } finally {
      setInstalling(false);
    }
  };

  // ------- Already installed -------
  if (isStandalone) {
    return (
      <Shell>
        <Icon />
        <StatusBadge tone="success">
          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
          Installed
        </StatusBadge>
        <h1 className="mt-4 font-serif text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-neutral-900">
          NContro is installed.
        </h1>
        <p className="mt-3 text-[15px] text-neutral-600 max-w-[420px]">
          {user ? "You’re all set. Tap below to jump back into the app." : "Sign in below to continue using NContro."}
        </p>
        <button
          data-testid="install-page-open-app"
          onClick={openApp}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F97316] hover:bg-[#EA6A11] active:bg-[#D55F0E] text-white text-[15px] font-semibold px-6 py-3.5 shadow-[0_10px_24px_-8px_rgba(249,115,22,0.55)] transition-colors"
        >
          {openLabel}
          <ExternalLink className="w-4 h-4" strokeWidth={2.4} />
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Icon />
      <StatusBadge>
        <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
        Get the app
      </StatusBadge>
      <h1 className="mt-4 font-serif text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-neutral-900">
        Install NContro.
      </h1>
      <p className="mt-3 text-[15px] text-neutral-600 max-w-[420px]">
        Plan, book and manage events from your home screen — faster launch and
        a fullscreen, app-like experience.
      </p>

      {/* -------- Primary CTA per platform -------- */}
      {isIos ? (
        <IosSteps />
      ) : hasDeferred ? (
        <div className="mt-8 w-full max-w-[420px]">
          <button
            data-testid="install-page-install-btn"
            onClick={handleInstall}
            disabled={installing}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F97316] hover:bg-[#EA6A11] active:bg-[#D55F0E] disabled:opacity-70 disabled:cursor-not-allowed text-white text-[15px] font-semibold px-6 py-3.5 shadow-[0_10px_24px_-8px_rgba(249,115,22,0.55)] transition-colors"
          >
            <Download className="w-4 h-4" strokeWidth={2.4} />
            {installing ? "Opening install…" : "Install NContro"}
          </button>
        </div>
      ) : supportsBip && canInstall === false ? (
        <NotYetEligible onOpen={openApp} openLabel={openLabel} />
      ) : (
        <UnsupportedBrowser onOpen={openApp} openLabel={openLabel} />
      )}

      {outcomeMessage && (
        <p data-testid="install-page-outcome" role="status" className="mt-3 max-w-[420px] text-[13px] text-neutral-500 text-center">
          {outcomeMessage}
        </p>
      )}

      {/* -------- Secondary "just open it" link -------- */}
      <button
        data-testid="install-page-open-in-browser"
        onClick={openApp}
        className="mt-8 text-[13px] font-medium text-neutral-500 hover:text-neutral-900 underline underline-offset-4 transition-colors"
      >
        {user ? "Or open NContro in the browser" : "Or sign in to NContro in the browser"}
      </button>
    </Shell>
  );
}

/* --------------------------- Sub-components --------------------------- */

function Shell({ children }) {
  return (
    <div
      data-testid="install-page"
      className="min-h-[70vh] flex flex-col items-center justify-center px-5 py-16 text-center"
      style={{ paddingTop: "max(4rem, env(safe-area-inset-top))" }}
    >
      {children}
    </div>
  );
}

function Icon() {
  return (
    <div className="relative">
      <div className="w-24 h-24 rounded-[22px] bg-white shadow-[0_18px_44px_-14px_rgba(249,115,22,0.45),0_2px_8px_-2px_rgba(0,0,0,0.06)] ring-1 ring-black/5 overflow-hidden">
        <img src="/icons/icon-192.png" alt="NContro" className="w-full h-full object-cover" />
      </div>
      <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center shadow-md">
        <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
      </span>
    </div>
  );
}

function StatusBadge({ children, tone = "brand" }) {
  const cls =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-600/15"
      : "bg-[#F97316]/10 text-[#C2410C] ring-[#F97316]/20";
  return (
    <div
      className={`mt-6 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold tracking-[0.15em] uppercase ring-1 ${cls}`}
    >
      {children}
    </div>
  );
}

function IosSteps() {
  return (
    <div
      data-testid="install-page-ios-steps"
      className="mt-8 w-full max-w-[420px] rounded-2xl bg-white ring-1 ring-black/5 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.15)] overflow-hidden text-left"
    >
      <div className="h-1 bg-gradient-to-r from-[#F97316] via-[#FB923C] to-[#F97316]" />
      <ol className="p-5 space-y-4">
        <Step
          n={1}
          title={
            <span className="inline-flex items-center gap-1.5">
              Tap the <Share className="w-4 h-4 inline" strokeWidth={2.2} /> Share button
            </span>
          }
          hint="It's at the bottom of Safari on iPhone, or top-right on iPad."
        />
        <Step
          n={2}
          title={
            <span className="inline-flex items-center gap-1.5">
              Choose <Plus className="w-4 h-4 inline" strokeWidth={2.2} /> Add to Home Screen
            </span>
          }
          hint='Scroll down in the share sheet if you don&rsquo;t see it.'
        />
        <Step n={3} title="Tap Add" hint="NContro will appear on your home screen." />
      </ol>
    </div>
  );
}

function Step({ n, title, hint }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-[#F97316] text-white text-[12px] font-bold flex items-center justify-center">
        {n}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-neutral-900 leading-snug">{title}</div>
        {hint && <div className="text-[12.5px] text-neutral-500 mt-0.5 leading-snug">{hint}</div>}
      </div>
    </li>
  );
}

function NotYetEligible({ onOpen, openLabel }) {
  return (
    <div
      data-testid="install-page-not-eligible"
      className="mt-8 w-full max-w-[420px] rounded-2xl bg-white ring-1 ring-black/5 p-5 text-left"
    >
      <div className="flex items-start gap-3">
        <Info className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" strokeWidth={2.2} />
        <div className="text-[13.5px] text-neutral-700 leading-relaxed">
          An installation prompt isn&rsquo;t available right now. You can continue
          in this browser using the button below, or use your browser&rsquo;s
          install option if available.
        </div>
      </div>
      <button
        data-testid="install-page-fallback-open"
        onClick={onOpen}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F97316] hover:bg-[#EA6A11] text-white text-[14px] font-semibold px-5 py-3 transition-colors"
      >
        {openLabel}
        <ExternalLink className="w-4 h-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}

function UnsupportedBrowser({ onOpen, openLabel }) {
  return (
    <div
      data-testid="install-page-unsupported"
      className="mt-8 w-full max-w-[420px] rounded-2xl bg-white ring-1 ring-black/5 p-5 text-left"
    >
      <div className="flex items-start gap-3">
        <Info className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" strokeWidth={2.2} />
        <div className="text-[13.5px] text-neutral-700 leading-relaxed">
          To install NContro on this device, please open this page in{" "}
          <span className="font-semibold">Chrome</span> or{" "}
          <span className="font-semibold">Edge</span> (Android &amp; desktop),
          or <span className="font-semibold">Safari</span> on iPhone / iPad.
        </div>
      </div>
      <button
        data-testid="install-page-unsupported-open"
        onClick={onOpen}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F97316] hover:bg-[#EA6A11] text-white text-[14px] font-semibold px-5 py-3 transition-colors"
      >
        {openLabel} in this browser
        <ExternalLink className="w-4 h-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}
